import { ReactNode, useEffect, useState, createContext, useContext, useRef } from 'react'
import { useTheme } from '@mui/material'
import {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeElementsOptionsMode
} from '@stripe/stripe-js'
import {
  elementsOptions,
  paymentElementOptions,
  stripePromise,
  expressCheckoutElementsOptions,
  expressCheckoutOptions
} from 'utils/stripe'

interface ElementsContextValue {
  stripe: Stripe | null
  elements: StripeElements | null
  expressCheckoutStripe: Stripe | null
  expressCheckoutElementsInstance: StripeElements | null
  expressCheckoutElement: StripeExpressCheckoutElement | null
  loadStripe?: () => void
  loadExpressCheckout?: () => void
}

const ElementsContext = createContext<ElementsContextValue>({
  stripe: null,
  elements: null,
  expressCheckoutStripe: null,
  expressCheckoutElementsInstance: null,
  expressCheckoutElement: null
})

export const Elements = ({ children }: { children: ReactNode }) => {
  const [shouldLoadStripe, setShouldLoadStripe] = useState(false)
  const [shouldLoadExpressCheckout, setShouldLoadExpressCheckout] = useState(false)
  const expressCheckoutLoadedRef = useRef(false)

  const theme = useTheme()

  const loadStripe = () => {
    setShouldLoadStripe(true)
  }

  const loadExpressCheckout = () => {
    setShouldLoadExpressCheckout(true)
  }

  const [context, setContext] = useState<ElementsContextValue>({
    stripe: null,
    elements: null,
    expressCheckoutStripe: null,
    expressCheckoutElementsInstance: null,
    expressCheckoutElement: null,
    loadStripe,
    loadExpressCheckout
  })

  // Load regular payment element
  useEffect(() => {
    if (shouldLoadStripe) {
      stripePromise.then((stripe) => {
        const elements = stripe?.elements(elementsOptions(theme)) || null
        elements?.create('payment', paymentElementOptions)
        setContext((prev) => ({ ...prev, stripe, elements }))
      })
    }
  }, [shouldLoadStripe, theme])

  // Load express checkout element
  useEffect(() => {
    if (shouldLoadExpressCheckout && !expressCheckoutLoadedRef.current) {
      expressCheckoutLoadedRef.current = true

      stripePromise.then((stripe) => {
        if (!stripe) return

        const elements = stripe.elements(
          expressCheckoutElementsOptions as StripeElementsOptionsMode
        ) as StripeElements

        const expressCheckoutElement = elements.create(
          'expressCheckout',
          expressCheckoutOptions
        ) as StripeExpressCheckoutElement

        setContext((prev) => ({
          ...prev,
          expressCheckoutStripe: stripe,
          expressCheckoutElementsInstance: elements,
          expressCheckoutElement
        }))
      })
    }
  }, [shouldLoadExpressCheckout])

  return <ElementsContext.Provider value={context}>{children}</ElementsContext.Provider>
}

export const useStripe = () => {
  const { stripe } = useContext(ElementsContext)
  return stripe
}

export const useElements = () => {
  const { elements } = useContext(ElementsContext)
  return elements
}

export const useLoadStripe = () => {
  const { loadStripe } = useContext(ElementsContext)
  return loadStripe
}

export const useExpressCheckoutStripe = () => {
  const { expressCheckoutStripe } = useContext(ElementsContext)
  return expressCheckoutStripe
}

export const useExpressCheckoutElementsInstance = () => {
  const { expressCheckoutElementsInstance } = useContext(ElementsContext)
  return expressCheckoutElementsInstance
}

export const useExpressCheckoutElement = () => {
  const { expressCheckoutElement } = useContext(ElementsContext)
  return expressCheckoutElement
}

export const useLoadExpressCheckout = () => {
  const { loadExpressCheckout } = useContext(ElementsContext)
  return loadExpressCheckout
}
